import React, { useState, useEffect } from 'react';
import { X, Users, Share2, Check } from 'lucide-react';

interface ShareModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (users: string[]) => void;
    teamUsers?: any[];
    currentSharedWith?: string[];
    itemName?: string;
}

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, onSave, teamUsers = [], currentSharedWith = [], itemName = 'Item' }) => {
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

    useEffect(() => {
        if (isOpen) {
            setSelectedUsers(currentSharedWith || []);
        }
    }, [isOpen, currentSharedWith]);

    if (!isOpen) return null;

    const shareableUsers = teamUsers.filter(u => u.role === 'editor' || u.role === 'admin' || u.role === 'sales');

    const toggleUser = (userEmail: string) => {
        setSelectedUsers(prev =>
            prev.includes(userEmail)
                ? prev.filter(email => email !== userEmail)
                : [...prev, userEmail]
        );
    };

    const handleSave = () => {
        onSave(selectedUsers);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md overflow-hidden border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                        <Share2 size={18} className="text-indigo-600" />
                        Share: {itemName}
                    </h3>
                    <button onClick={onClose} className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 cursor-pointer">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6">
                    <p className="text-sm text-slate-500 mb-4">
                        Select team members who can view or edit this item.
                    </p>

                    {shareableUsers.length === 0 ? (
                        <div className="py-8 text-center text-slate-400">
                            <Users size={32} className="mx-auto mb-2 opacity-40" />
                            <p className="text-sm">No team members found.</p>
                        </div>
                    ) : (
                        <div className="space-y-1 max-h-60 overflow-y-auto">
                            {shareableUsers.map(u => (
                                <div
                                    key={u.id || u.email}
                                    onClick={() => toggleUser(u.email)}
                                    className={`flex items-center gap-3 p-3 rounded-md cursor-pointer transition-colors ${selectedUsers.includes(u.email)
                                        ? 'bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700'
                                        : 'hover:bg-slate-50 dark:hover:bg-slate-700/50 border border-transparent'
                                        }`}
                                >
                                    <div className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${selectedUsers.includes(u.email)
                                        ? 'bg-indigo-600 border-indigo-600 text-white'
                                        : 'border-slate-300 dark:border-slate-600'
                                        }`}>
                                        {selectedUsers.includes(u.email) && <Check size={14} />}
                                    </div>
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-xs">
                                        {u.name?.charAt(0) || 'U'}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{u.name}</p>
                                        <p className="text-xs text-slate-500 truncate">{u.email}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50">
                    <span className="text-xs text-slate-500">
                        {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
                    </span>
                    <div className="flex gap-3">
                        <button onClick={onClose} className="px-4 py-2 text-sm text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded-md cursor-pointer">Cancel</button>
                        <button onClick={handleSave} className="px-4 py-2 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-md cursor-pointer">Save Sharing</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShareModal;
